import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Typography, Box, Chip, Breadcrumbs, Link, Skeleton, Paper, Divider, alpha, useTheme,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { getArticle } from '../../utils/api';
import { Article } from '../../utils/types';

const ArticleView: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const theme = useTheme();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      if (!slug) return;
      try {
        const data = await getArticle(slug);
        setArticle(data);
      } catch (err) {
        setError('Article not found');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  if (loading) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        <Skeleton variant="text" width={200} height={30} />
        <Skeleton variant="text" width="80%" height={50} sx={{ mt: 2 }} />
        <Skeleton variant="rounded" height={400} sx={{ mt: 3 }} />
      </Box>
    );
  }

  if (error || !article) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h4" gutterBottom>Article not found</Typography>
        <Link component={RouterLink} to="/">Back to Help Center</Link>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/" underline="hover" color="inherit">Help Center</Link>
        <Link component={RouterLink} to={`/category/${article.category}`} underline="hover" color="inherit">
          {article.category}
        </Link>
        <Typography color="text.primary">{article.title}</Typography>
      </Breadcrumbs>

      <Typography variant="h3" fontWeight={700} gutterBottom>
        {article.title}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Chip
          label={article.category}
          size="small"
          sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {new Date(article.updatedAt).toLocaleDateString()}
          </Typography>
        </Box>
      </Box>

      {article.videoUrl && (
        <Paper elevation={0} sx={{ mb: 4, borderRadius: 2, overflow: 'hidden', border: '1px solid #E0E0E0' }}>
          <video
            controls
            width="100%"
            src={article.videoUrl}
            style={{ display: 'block' }}
          />
        </Paper>
      )}

      <Divider sx={{ mb: 4 }} />

      <Box
        sx={{
          '& img': { maxWidth: '100%', borderRadius: 1 },
          '& pre': { bgcolor: '#F5F5F5', p: 2, borderRadius: 1, overflow: 'auto' },
          '& code': { fontFamily: 'monospace', fontSize: 14 },
          '& table': { borderCollapse: 'collapse', width: '100%' },
          '& th, & td': { border: '1px solid #E0E0E0', p: 1 },
          '& blockquote': { borderLeft: `4px solid ${theme.palette.primary.main}`, pl: 2, ml: 0, color: 'text.secondary' },
          '& a': { color: theme.palette.primary.main },
          lineHeight: 1.8,
          fontSize: '1.05rem',
        }}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
          {article.content}
        </ReactMarkdown>
      </Box>

      {article.tags?.length > 0 && (
        <Box sx={{ mt: 4, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {article.tags.map((tag) => (
            <Chip key={tag} label={tag} size="small" variant="outlined" />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ArticleView;
